import { gestureReducer, type GestureEvent } from '../audio/gesture.ts';
import { msg, type Message, type PlainKey } from '../i18n/t.ts';
import { Recorder, SAMPLE_RATE, type BridgeLike, type RecorderEvent, type Timers } from './sayItBack.ts';

/**
 * Say it with me's logic, out of the `.tsx` so it runs under `node --test`
 * (kid-practice §5). Nothing here imports Relay: the recording is played
 * back from page memory and never leaves the tab (§5.4).
 */

/**
 * `tutor_audio::encode_pcm16`'s 44-byte header, in TypeScript. That function
 * is not in the wasm surface, and exporting it would pull the wasm package
 * into the main-thread bundle to save twenty lines (§5.3).
 */
export function wavFromPcm(pcm: Int16Array, rate: number): Uint8Array {
  const out = new Uint8Array(44 + pcm.length * 2);
  const view = new DataView(out.buffer);
  const ascii = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(at + i, s.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + pcm.length * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, pcm.length * 2, true);
  pcm.forEach((s, i) => view.setInt16(44 + i * 2, s, true));
  return out;
}

export type SayWithMePhase = 'rest' | 'opening' | 'recording' | 'playing' | 'done';

/** Every label the button can wear, so the view can stack them in one cell. */
export const LABELS: Record<SayWithMePhase, PlainKey> = {
  rest: 'say-with-me-button',
  done: 'say-with-me-button',
  opening: 'say-with-me-starting',
  recording: 'say-with-me-recording',
  playing: 'say-with-me-playing',
};

/**
 * The one line under the buttons. "That was you!" is true whatever was
 * recorded, because it is their recording: praise for the loop, never for
 * the attempt's content, which nothing here can know (§5.1).
 */
export function statusFor(phase: SayWithMePhase, error: Message | null): { text: Message; alert: boolean } | null {
  if (error) return { text: error, alert: true };
  return phase === 'done' ? { text: msg('say-with-me-done'), alert: false } : null;
}

/** The slice of `HTMLAudioElement` this plays through. */
export type AudioLike = {
  readonly ended: boolean;
  play(): Promise<void>;
  pause(): void;
  onended: ((e: Event) => void) | null;
  onerror: ((e: Event | string) => void) | null;
  onpause: ((e: Event) => void) | null;
};

export type Playback = {
  url(wav: Uint8Array): string;
  revoke(url: string): void;
  audio(url: string): AudioLike;
  /**
   * `oneVoice`: the child's clip pauses the tutor, and the reverse. `false`
   * while a recording holds the page (kid-practice §5.3 amendment M4): the
   * caller must not go on to `play()`.
   */
  claim(a: AudioLike): boolean;
  /**
   * A recording is starting: nothing may play until the returned release
   * runs (`oneVoice.hold`).
   */
  hold(): () => void;
};

type MakeBridge = (onPcm: (p: Int16Array) => void, onEnded: () => void) => BridgeLike;

/**
 * Tap to start, tap to stop, hear it back (§5.1).
 *
 * The microphone is open only while recording. Each attempt builds its own
 * `Recorder`, and disposes it the moment the clip is complete, before
 * playback: `Recorder.dispose` stops the tracks and closes the context. On an
 * iPhone an open capture stream routes output to the earpiece
 * (`session.ts`), so both the playback and "Hear it" would otherwise be
 * quiet for as long as the card stayed up. The price is opening the
 * microphone again on every attempt (the as-built amendment).
 */
export class SayWithMe {
  private phase: SayWithMePhase = 'rest';
  private recorder: Recorder | null = null;
  private playing: { audio: AudioLike; url: string } | null = null;
  private disposed = false;
  // Set for the span of one attempt, from `start()` to wherever recording
  // ends (see `endHold`'s call sites): idle between attempts, unlike
  // `oneVoice`'s own hold count, which this is the release closure for.
  private releaseHold: (() => void) | null = null;

  // Assigned in the body, for `Recorder`'s reason: node's strip-only loader
  // rejects parameter properties.
  private readonly make: MakeBridge;
  private readonly playback: Playback;
  private readonly emit: (phase: SayWithMePhase, error: Message | null) => void;
  private readonly timers: Timers | undefined;

  constructor(
    make: MakeBridge,
    playback: Playback,
    emit: (phase: SayWithMePhase, error: Message | null) => void,
    timers?: Timers,
  ) {
    this.make = make;
    this.playback = playback;
    this.emit = emit;
    this.timers = timers;
  }

  /** The kid gesture (D5): a tap toggles, a release is inert, a cancel stops. */
  gesture(e: GestureEvent): void {
    // Nothing starts over the child's own voice playing back.
    if (this.disposed || this.phase === 'playing') return;
    const capturing = this.phase === 'opening' || this.phase === 'recording';
    const next = gestureReducer('kid', { capturing }, e).capturing;
    if (next && !capturing) this.start();
    else if (!next && capturing) this.stop();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.closeMicrophone();
    this.endHold();
    this.endPlayback();
  }

  private start(): void {
    this.endPlayback();
    this.releaseHold = this.playback.hold();
    const r = new Recorder(this.make, (e) => this.onRecorder(r, e), this.timers);
    this.recorder = r;
    // Held until the second tap, so a first-ever permission prompt still
    // records once it is granted (say-it-back amendment 8).
    r.press();
  }

  private stop(): void {
    if (this.phase === 'opening') {
      // A second tap before the microphone opened: nothing was said, and an
      // idle open microphone is what the class exists not to leave behind.
      this.closeMicrophone();
      this.endHold();
      this.set('rest', null);
    } else {
      this.recorder?.release();
    }
  }

  private onRecorder(r: Recorder, e: RecorderEvent): void {
    if (r !== this.recorder) return;
    switch (e.kind) {
      case 'starting':
        this.set('opening', null);
        break;
      case 'recording':
        this.set('recording', null);
        break;
      case 'sending':
        this.set('playing', null);
        break;
      case 'clip':
        this.closeMicrophone();
        // Before `play()`: the child's own clip must claim `oneVoice`
        // successfully, and a hold still open would refuse it (M4).
        this.endHold();
        this.play(e.pcm);
        break;
      case 'tooShort':
        this.closeMicrophone();
        this.endHold();
        this.set('rest', msg('say-with-me-too-short'));
        break;
      case 'micError':
        this.closeMicrophone();
        this.endHold();
        this.set('rest', msg('say-with-me-mic'));
        break;
      case 'ready':
        break;
    }
  }

  private play(pcm: Int16Array): void {
    const url = this.playback.url(wavFromPcm(pcm, SAMPLE_RATE));
    const audio = this.playback.audio(url);
    this.playing = { audio, url };
    const rest = () => {
      if (this.playing?.audio !== audio) return;
      this.endPlayback();
      this.set('rest', null);
    };
    audio.onended = () => {
      if (this.playing?.audio !== audio) return;
      this.endPlayback();
      this.set('done', null);
    };
    audio.onerror = rest;
    // A pause that is not the end is "Hear it" taking over (`oneVoice`). A
    // media element fires `pause` just before `ended`, hence the check.
    audio.onpause = () => {
      if (!audio.ended) rest();
    };
    if (!this.playback.claim(audio)) return;
    audio.play().catch(rest);
  }

  private closeMicrophone(): void {
    this.recorder?.dispose();
    this.recorder = null;
  }

  /** Idempotent: every path an attempt can end by calls this once. */
  private endHold(): void {
    this.releaseHold?.();
    this.releaseHold = null;
  }

  private endPlayback(): void {
    if (!this.playing) return;
    const { audio, url } = this.playing;
    this.playing = null;
    audio.onended = audio.onerror = audio.onpause = null;
    audio.pause();
    this.playback.revoke(url);
  }

  private set(phase: SayWithMePhase, error: Message | null): void {
    this.phase = phase;
    this.emit(phase, error);
  }
}
